import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { UNAUTHORIZED_STATUS } from './constant';
import { getCookie } from './cookies';

// Centralized unauthorized handling
const handleUnauthorized = async () => {
  // Skip logout if disabled via environment variable
  if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_BYPASS_AUTH === 'true') {
    return;
  } else {
    try {
      const { signOut } = await import('next-auth/react');
      await signOut({
        redirect: false,
      }).then(() => {
        if (typeof window !== 'undefined') {
          localStorage.clear();
          document.cookie.split(';').forEach((cookie) => {
            const cookieName = cookie.split('=')[0].trim();
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
          });
          window.location.href = '/';
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: still clear local data and redirect
      if (typeof window !== 'undefined') {
        localStorage.clear();
        window.location.href = '/';
      }
    }
  }
};

export interface ApiConfig {
  baseURL: string;
  timeout?: number;
  customHeaders?: Record<string, string>;
  onUnauthorized?: () => Promise<void>;
}

export const createConfiguredApiInstance = ({
  baseURL,
  timeout = 120000,
  customHeaders = {},
  onUnauthorized = handleUnauthorized,
}: ApiConfig): AxiosInstance => {
  const instance = axios.create({
    timeout,
    baseURL,
    headers: {
      Accept: 'application/json',
      ...customHeaders,
    },
    // Automatically encode query parameters to handle special characters like &, =, ?, etc.
    paramsSerializer: {
      encode: (param: string) => encodeURIComponent(param),
    },
  });

  // Request interceptor
  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const accessToken = await getCookie('access-token');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });

  // Response interceptor with custom unauthorized handler
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      if (error.response && error.response.status === UNAUTHORIZED_STATUS) {
        await onUnauthorized();
      }
      return Promise.reject(error instanceof Error ? error : new Error('Request failed'));
    },
  );

  return instance;
};
